import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, LockKeyhole, ArrowUpRight } from "lucide-react";
import { useClientAuth, formatApiErrorDetail } from "../lib/auth";

export default function LoginPage() {
  const { login } = useClientAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const next = params.get("next") || "/checklist";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="client-login-page"
      className="min-h-[75vh] bg-[#F8F9FA] bg-grid flex items-center justify-center px-5 py-14"
    >
      <form
        onSubmit={submit}
        data-testid="client-login-form"
        className="w-full max-w-md bg-white border-2 border-[#1A1A1A] rounded-3xl p-8 nb-shadow-lg"
      >
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border-2 border-[#1A1A1A] bg-[#FFD700] nb-shadow-sm">
          <LockKeyhole size={22} strokeWidth={2.5} />
        </span>
        <h1 className="mt-5 font-display font-black text-3xl md:text-4xl tracking-tighter">
          Client login.
        </h1>
        <p className="mt-2 text-sm font-semibold text-[#4A4A4A]">
          The Inventory Checklist, Equipment Guide and Facilitator Certification
          are available to active TinkerShift clients.
        </p>

        {error && (
          <p
            data-testid="client-login-error"
            className="mt-4 bg-[#FF007F]/10 border-2 border-[#FF007F] text-[#1A1A1A] rounded-xl px-4 py-3 text-sm font-semibold"
          >
            {error}
          </p>
        )}

        <label className="mt-5 block">
          <span className="font-mono text-[11px] uppercase tracking-widest text-[#4A4A4A]">
            Email
          </span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@school.edu.au"
            data-testid="client-email-input"
            className="mt-1.5 w-full border-2 border-[#1A1A1A] rounded-xl px-4 py-3 font-semibold focus:outline-none focus:ring-4 focus:ring-[#FFD700]"
          />
        </label>
        <label className="mt-4 block">
          <span className="flex items-center justify-between">
            <span className="font-mono text-[11px] uppercase tracking-widest text-[#4A4A4A]">
              Password
            </span>
            <Link
              to="/forgot-password"
              data-testid="forgot-password-link"
              className="font-mono text-[11px] uppercase tracking-widest text-[#0055FF] hover:text-[#FF007F] transition-colors"
            >
              Forgot your password?
            </Link>
          </span>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your client password"
            data-testid="client-password-input"
            className="mt-1.5 w-full border-2 border-[#1A1A1A] rounded-xl px-4 py-3 font-semibold focus:outline-none focus:ring-4 focus:ring-[#FFD700]"
          />
        </label>

        <button
          type="submit"
          disabled={busy || !email || !password}
          data-testid="client-login-submit"
          className="mt-6 inline-flex items-center justify-center gap-2 w-full bg-[#1A1A1A] text-white border-2 border-[#1A1A1A] rounded-full px-5 py-3 font-bold uppercase tracking-wide nb-shadow-sm nb-press disabled:opacity-70"
        >
          {busy ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <ArrowUpRight size={16} strokeWidth={2.5} />
          )}
          Sign in
        </button>

        <p className="mt-5 text-xs font-semibold text-[#4A4A4A] leading-relaxed">
          Client access is by invitation with an active TinkerShift plan.
          Need an account? Email{" "}
          <a
            href="mailto:hello@tinkertank.rocks"
            className="text-[#0055FF] underline underline-offset-2"
          >
            hello@tinkertank.rocks
          </a>{" "}
          or <Link to="/contact" className="text-[#0055FF] underline underline-offset-2">get in touch</Link>.
        </p>
      </form>
    </div>
  );
}
